// src/pages/OwnerFundsEntry.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/Button";
import { AppleComboBox } from "../components/AppleComboBox";
import { apiGet, apiPost } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { saveDraft, loadDraft, clearDraft } from "../utils/localStorageDraft";
import { ymdTodayLocal } from "../utils/dateUtils";
import { A } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import { extractApiDetailOutcome, isClosedPeriodError } from "../utils/closedPeriod";
import {
  IDLE_OWNER_FUNDS_CREATE_ATTEMPT,
  OwnerFundsCreateAttempt,
  canonicalOwnerFundsCreate,
  fingerprintOwnerFundsCreate,
  hydrateOwnerFundsCreateAttempt,
  randomUuidKey,
  decideOwnerFundsCreateAttempt,
  resolveOwnerFundsCreateSuccess,
  resolveOwnerFundsCreateDefiniteRejection,
  resolveOwnerFundsCreateAmbiguousFailure,
  shouldRestoreAmbiguousAttempt,
  classifyOwnerFundsCreateFailure,
} from "../utils/ownerFundsCreateIdempotency";

type TxType = "advance_to_company" | "withdraw_from_company";

type Account = {
  id: number;
  name: string;
  type: string; // "asset" | "liability" | "equity" | ...
  kind?: string | null;
  is_system?: boolean;
};

type Opt<T extends string = string> = { id: T; label: string };

const TX_OPTIONS: Opt<TxType>[] = [
  {
    id: "advance_to_company",
    label: "Advance to company (owner adds money in)",
  },
  {
    id: "withdraw_from_company",
    label: "Withdraw from company (owner takes money out)",
  },
];

const DRAFT_KEY = "ricebook_ownerfunds_draft_v2";
const today = () => ymdTodayLocal();

const norm = (s?: string | null) =>
  (s || "").toLowerCase().replace(/['’]/g, "").replace(/\s+/g, " ").trim();

function findShareholderAdvanceId(all: Account[]): number | null {
  const byKind = all.find((a) => norm(a.kind) === "shareholder_advance");
  if (byKind) return byKind.id;

  const byName = all.find((a) => {
    const n = norm(a.name);
    return (
      n.includes("shareholder advance") ||
      (n.includes("advance") && n.includes("shareholder"))
    );
  });
  return byName ? byName.id : null;
}

function findDueFromShareholderId(all: Account[]): number | null {
  const byKind = all.find((a) => norm(a.kind) === "due_from_shareholder");
  if (byKind) return byKind.id;

  const byName = all.find((a) => {
    const n = norm(a.name);
    return (
      n.includes("due from shareholder") ||
      (n.includes("due from") && n.includes("shareholder"))
    );
  });
  return byName ? byName.id : null;
}

function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

type TextEl = HTMLInputElement | HTMLTextAreaElement;
function selectAllOnFocus<T extends TextEl>(
  e: React.FocusEvent<T> | React.MouseEvent<T>
) {
  const el = e.currentTarget;
  setTimeout(() => el.select(), 0);
}
function focusAndSelectOnMouseDown<T extends TextEl>(e: React.MouseEvent<T>) {
  const el = e.currentTarget;
  if (document.activeElement !== el) {
    e.preventDefault();
    el.focus();
    el.select();
  }
}

type OwnerFundsMessage = string | { key: ParseKeys };
const localMessage = (key: ParseKeys): OwnerFundsMessage => ({ key });

// Keep raw API text distinct from our fallback, even when the text is identical.
function apiFailureMessage(err: unknown): OwnerFundsMessage {
  const detail = extractApiDetailOutcome(err);
  return detail.kind === "raw"
    ? detail.text || localMessage("ownerFunds.errors.post")
    : localMessage("sales.errors.generic");
}

export default function OwnerFundsEntry() {
  const { t } = useTranslation();
  const displayMessage = (message: OwnerFundsMessage): string => typeof message === "string"
    ? message
    : message.key === "sales.errors.closedSubmit"
      ? t(message.key, { date: t("sales.errors.closedDate") })
      : t(message.key);
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [date, setDate] = useState<string>(today());
  const [txType, setTxType] = useState<TxType>("advance_to_company");
  const [amount, setAmount] = useState<string>("");
  const [paymentAccountId, setPaymentAccountId] = useState<string>(""); // cash/bank
  const [description, setDescription] = useState<string>("");

  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<Account[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<OwnerFundsMessage>("");

  // Owner-Funds-create idempotency attempt lifecycle (see
  // utils/ownerFundsCreateIdempotency.ts). A ref, not state: must be stable
  // across renders, must not itself trigger a re-render, and must be
  // readable synchronously by the in-flight guard below. The draft also
  // persists this attempt so remounting preserves uncertain retry identity.
  const ownerFundsAttemptRef = useRef<OwnerFundsCreateAttempt>(
    IDLE_OWNER_FUNDS_CREATE_ATTEMPT
  );
  // Purely synchronous double-click/repeat-tap guard -- independent of the
  // attempt state machine above.
  const ownerFundsInFlightRef = useRef(false);
  const draftAutosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftCompletedRef = useRef(false);

  const persistAttempt = (attempt: OwnerFundsCreateAttempt) => {
    ownerFundsAttemptRef.current = attempt;
    // Persist current fields too: submit can precede the debounced autosave.
    saveDraft(DRAFT_KEY, {
      date, txType, amount, paymentAccountId, description,
      attempt: attempt.state === "idle" ? null : attempt,
    });
  };

  const stopDraftAutosave = () => {
    draftCompletedRef.current = true;
    if (draftAutosaveTimerRef.current !== null) {
      clearTimeout(draftAutosaveTimerRef.current);
      draftAutosaveTimerRef.current = null;
    }
  };

  const labelCls = `block ${A.label} mb-1`;
  const requiredStar = <span className="text-red-500">*</span>;

  // ---- Load accounts ----
  async function loadAccounts() {
    const accs = await apiGet<Account[]>("/accounts");
    setAllAccounts(accs || []);

    const pays = (accs || [])
      .filter((a) => {
        const t = norm(a.type);
        const k = norm(a.kind);
        return t === "asset" && (k === "cash" || k === "bank") && !a.is_system;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    setPaymentAccounts(pays);
  }

  useEffect(() => {
    loadAccounts();

    const d = loadDraft(DRAFT_KEY);
    if (d) {
      setDate(d.date ?? today());
      setTxType(d.txType ?? "advance_to_company");
      setAmount(d.amount ?? "");
      setPaymentAccountId(d.paymentAccountId ?? "");
      setDescription(d.description ?? "");
      ownerFundsAttemptRef.current = hydrateOwnerFundsCreateAttempt(d.attempt);
    }

    const onFocus = () => loadAccounts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Read the live attempt so a queued autosave cannot overwrite its identity.
  useEffect(() => {
    if (draftCompletedRef.current) return;
    const t = setTimeout(() => {
      draftAutosaveTimerRef.current = null;
      if (draftCompletedRef.current) return;
      saveDraft(DRAFT_KEY, {
        date,
        txType,
        amount,
        paymentAccountId,
        description,
        attempt: ownerFundsAttemptRef.current.state === "idle"
          ? null : ownerFundsAttemptRef.current,
      });
    }, 250);
    draftAutosaveTimerRef.current = t;
    return () => clearTimeout(t);
  }, [date, txType, amount, paymentAccountId, description]);

  // ---- Amount sanitization ----
  const sanitizeAmount = (v: string) => {
    v = v.replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
    return v;
  };

  const onAmountChange = (v: string) => setAmount(sanitizeAmount(v));

  const onAmountBlur = () => {
    let v = amount;
    if (!v) return;

    if (v.includes(".")) {
      const [i, d] = v.split(".");
      v = i + (d ? "." + d.slice(0, 2) : "");
    }
    if (v.endsWith(".")) v = v.slice(0, -1);
    setAmount(v);
  };

  // ---- Combos ----
  const paymentOpts: Opt<string>[] = useMemo(
    () => paymentAccounts.map((a) => ({ id: String(a.id), label: a.name })),
    [paymentAccounts]
  );
  const selectedPaymentOpt =
    paymentOpts.find((o) => o.id === String(paymentAccountId)) || null;
  const selectedTxOpt = TX_OPTIONS.find((o) => o.id === txType) || null;

  const canSubmit = Number(amount) > 0 && !!paymentAccountId && !saving;

  // Force the form fields back to EXACTLY what was submitted under an
  // ambiguous (still-unresolved) attempt -- used both when a mismatched
  // edited retry is blocked, and right after an ambiguous failure is
  // detected. Mirrors pages/deposits/NewDeposit.tsx's
  // restoreSubmittedPayload: necessary because this page's inputs are
  // never disabled while a request is in flight, so the user could have
  // kept typing during the request.
  const restoreSubmittedPayload = (attempt: OwnerFundsCreateAttempt) => {
    if (!shouldRestoreAmbiguousAttempt(attempt) || !attempt.submittedPayload) {
      return;
    }
    const payload = attempt.submittedPayload;

    setDate(payload.date);
    setTxType(
      payload.direction === "owner_to_company"
        ? "advance_to_company"
        : "withdraw_from_company"
    );
    setPaymentAccountId(String(payload.bank_account_id));
    setAmount(payload.amount);
    setDescription(payload.description || "");
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `saving` state alone can't catch this since
    // it only updates on the next render. Independent of the attempt state
    // machine below.
    if (ownerFundsInFlightRef.current) return;

    if (!paymentAccountId)
      return setError(localMessage("ownerFunds.errors.selectAccount"));
    if (!amount || Number(amount) <= 0)
      return setError(localMessage("ownerFunds.errors.positiveAmount"));

    const shareholderAdvanceId = findShareholderAdvanceId(allAccounts);
    const dueFromShareholderId = findDueFromShareholderId(allAccounts);

    const shareholderAccountId =
      txType === "advance_to_company"
        ? shareholderAdvanceId
        : dueFromShareholderId;

    if (!shareholderAccountId) {
      return setError(
        localMessage("ownerFunds.errors.setupAccounts")
      );
    }

    const direction =
      txType === "advance_to_company" ? "owner_to_company" : "company_to_owner";
    const amt = round2(Number(amount));

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether this is a first attempt (mint a new
    // key), an unchanged retry of a still-ambiguous attempt (reuse its
    // key), or an edited retry of one (block, no network call, restore the
    // original submitted values).
    const canonicalPayload = canonicalOwnerFundsCreate(
      date,
      Number(paymentAccountId),
      shareholderAccountId,
      amt,
      "loan",
      direction,
      "",
      description || ""
    );
    const fingerprint = fingerprintOwnerFundsCreate(canonicalPayload);
    const decision = decideOwnerFundsCreateAttempt(
      ownerFundsAttemptRef.current,
      canonicalPayload,
      fingerprint,
      randomUuidKey
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been recorded
      // -- never silently mint a new key or retry with edited details
      // under the same one. Restore the exact values that were actually
      // submitted so the next click safely reuses the key.
      restoreSubmittedPayload(ownerFundsAttemptRef.current);
      setError(localMessage("ownerFunds.retry.editedBlocked"));
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- ownerFundsInFlightRef.current already
      // returns above before this point. Kept for correctness of the pure
      // decision function itself.
      return;
    }

    ownerFundsAttemptRef.current = decision.attempt;
    ownerFundsInFlightRef.current = true;

    setSaving(true);
    try {
      persistAttempt(decision.attempt);
      // Send the SAME canonicalPayload fields used to build the
      // fingerprint above -- never separately reconstructed -- plus
      // idempotency_key.
      await apiPost("/pro/owner-funds", {
        ...canonicalPayload,
        idempotency_key: decision.idempotencyKey,
      });

      // Confirmed success (create or replay -- both return the same
      // response shape): clear the attempt so the next entry gets a fresh
      // key instead of leaking this one into it.
      ownerFundsAttemptRef.current = resolveOwnerFundsCreateSuccess();
      stopDraftAutosave();
      clearDraft(DRAFT_KEY);
      navigate(companyRoute(company_id!, "/owner-funds"));
    } catch (err: any) {
      const failureClass = classifyOwnerFundsCreateFailure(err, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before
        // any claim/business write commits (same savepoint as every other
        // create()-time HTTPException), so nothing was persisted. Clear
        // the attempt so a corrected date can retry under a new key.
        persistAttempt(resolveOwnerFundsCreateDefiniteRejection());
        setError(localMessage("sales.errors.closedSubmit"));
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never succeed
        // for this payload -- clear it so the next attempt mints a fresh
        // one.
        persistAttempt(resolveOwnerFundsCreateDefiniteRejection());
        setError(localMessage("ownerFunds.retry.conflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        // HTTP 400/401/403/422 -- nothing persisted. Clear the attempt so
        // the user can edit and retry under a new key.
        persistAttempt(resolveOwnerFundsCreateDefiniteRejection());
        setError(apiFailureMessage(err));
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload so an unchanged retry reuses the same key
      // and safely replays instead of risking a duplicate, then restore
      // the form to exactly what was submitted (overwriting any edit made
      // while the request was in flight).
      ownerFundsAttemptRef.current = resolveOwnerFundsCreateAmbiguousFailure(
        ownerFundsAttemptRef.current
      );
      restoreSubmittedPayload(ownerFundsAttemptRef.current);
      setError(apiFailureMessage(err));
    } finally {
      ownerFundsInFlightRef.current = false;
      setSaving(false);
    }
  }

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center justify-start px-2 py-6`}
    >
      {/* Header (Apple-style) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center gap-2 border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} text-center`}>
            {t("ownerFunds.title")}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id!, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Form */}
      <form
        onSubmit={onSubmit}
        className={`${A.card} max-w-3xl w-full p-5 sm:p-6`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Type */}
          <div className="lg:col-span-2">
            <label className={labelCls}>{t("directory.products.type")} {requiredStar}</label>
            <AppleComboBox<Opt<TxType>>
              options={TX_OPTIONS}
              value={selectedTxOpt}
              onChange={(o) => setTxType(o ? o.id : "advance_to_company")}
              getOptionLabel={(o) => !o ? "" : o.id === "advance_to_company"
                ? t("ownerFunds.entry.advanceToCompany") : t("ownerFunds.entry.withdrawFromCompany")}
              getOptionSearchText={(o) => o.label}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              placeholder={t("ownerFunds.entry.selectType")}
            />
          </div>

          {/* Cash/Bank */}
          <div className="lg:col-span-2">
            <label className={labelCls}>{t("ownerFunds.entry.cashBank")} {requiredStar}</label>
            <AppleComboBox<Opt<string>>
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              options={paymentOpts}
              value={selectedPaymentOpt}
              onChange={(o) => setPaymentAccountId(o ? o.id : "")}
              getOptionLabel={(o) => (o ? o.label : "")}
              placeholder={
                paymentOpts.length
                  ? t("ownerFunds.entry.selectAccount")
                  : t("ownerFunds.entry.noAccounts")
              }
            />
          </div>

          {/* Amount */}
          <div>
            <label className={labelCls}>{t("sales.common.amount")} {requiredStar}</label>
            <input
              type="text"
              inputMode="decimal"
              className={`${A.inputBase} text-right ${A.num}`}
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              onBlur={onAmountBlur}
              onFocus={selectAllOnFocus}
              onMouseDown={focusAndSelectOnMouseDown}
              placeholder="0.00"
              required
            />
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>{t("sales.common.date")} {requiredStar}</label>

            <AppleDateInput
              valueYmd={date}
              onChangeYmd={setDate}
              allowEmpty={false} // because Date is required
              maxYmd={today()} // same as max={today()}
              ariaLabel={t("sales.common.date")}
              openCalendarLabel={t("sales.shared.openCalendar")}
              className={A.num} // optional: tabular numeric look
            />
          </div>

          {/* Description */}
          <div className="lg:col-span-4">
            <label className={labelCls}>{t("sales.edit.description")} {requiredStar}</label>
            <input
              type="text"
              className={A.inputBase}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("ownerFunds.entry.notePlaceholder")}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              clearDraft(DRAFT_KEY);
              navigate(companyRoute(company_id!, "/owner-funds"));
            }}
          >
            {t("directory.common.cancel")}
          </Button>

          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {saving ? t("ownerFunds.entry.posting") : t("ownerFunds.entry.post")}
          </Button>
        </div>

        {/* Error (reserve space) */}
        <div style={{ minHeight: 24 }} className="mt-2">
          {error ? (
            <div className="text-red-600 text-[13px] font-medium">{displayMessage(error)}</div>
          ) : null}
        </div>
      </form>
    </div>
  );
}
